/**
 * QualityChipStripDrawer — Sprint A.1
 * Slide-out detail panel for a single quality measure chip.
 * Shows evidence, status, and available actions.
 */
import React, { useState } from 'react';
import { api } from '../../services/api';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { X, Check, RefreshCw, ShieldCheck, Calendar, AlertCircle, Mic, ClipboardList, FileText } from 'lucide-react';
import { toast } from 'sonner';
import { CHIP_STYLES } from './QualityChipStrip';

const SOURCE_ICONS = {
  transcript_signal: <Mic className="w-3 h-3 text-blue-500" />,
  intake_form:       <ClipboardList className="w-3 h-3 text-teal-500" />,
  vitals:            <ShieldCheck className="w-3 h-3 text-emerald-500" />,
  document:          <FileText className="w-3 h-3 text-orange-500" />,
  order:             <Check className="w-3 h-3 text-violet-500" />,
};

function EvidenceEntry({ entry }) {
  return (
    <div className="flex gap-2 py-1.5 border-b border-dashed last:border-0">
      <div className="shrink-0 mt-0.5">{SOURCE_ICONS[entry.source] || SOURCE_ICONS.transcript_signal}</div>
      <div className="flex-1 min-w-0">
        {entry.label && <p className="text-xs font-medium text-foreground">{entry.label}</p>}
        {entry.snippet && (
          <blockquote className="text-[10px] text-muted-foreground italic border-l-2 border-violet-300 pl-2 mt-0.5 line-clamp-3">
            "{entry.snippet}"
          </blockquote>
        )}
        <p className="text-[9px] text-muted-foreground mt-0.5">
          {entry.source?.replace(/_/g, ' ')} · conf {Math.round((entry.confidence || 0) * 100)}%
          {entry.captured_at && ` · ${new Date(entry.captured_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`}
        </p>
      </div>
    </div>
  );
}

export function QualityChipStripDrawer({ chip, encounterId, onClose, onActionTaken }) {
  const [executing, setExecuting] = useState(null);

  if (!chip) return null;

  const style = CHIP_STYLES[chip.status] || CHIP_STYLES.due;

  const handleAction = async (action) => {
    if (!encounterId) {
      toast.error('Action requires an active encounter');
      return;
    }
    setExecuting(action.id);
    try {
      const signalId = chip.evidence_this_visit?.[0]?.signal_id || '';
      await api.post(`/encounters/${encounterId}/quality-chip-action`, {
        measure_id: chip.measure_id,
        action_id: action.id,
        signal_id: signalId,
      });
      toast.success(
        action.id === 'confirm_decline' ? `Decline documented for ${chip.short_label}`
        : action.id === 'rediscuss' ? 'Will re-evaluate at next visit'
        : `${chip.short_label} order staged`
      );
      if (onActionTaken) onActionTaken(chip.measure_id, action.id);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Action failed');
    } finally {
      setExecuting(null);
    }
  };

  const STATUS_LABEL = {
    compliant:            'Compliant',
    due:                  'Due this period',
    overdue:              'Overdue',
    staged:               'Order staged this visit',
    pending_confirmation: 'Awaiting physician confirmation',
  };

  return (
    <div className="fixed inset-y-0 right-0 w-80 bg-card border-l shadow-2xl z-50 flex flex-col animate-in slide-in-from-right-2 duration-200"
      data-testid={`quality-drawer-${chip.measure_id}`}>
      {/* Header */}
      <div className="flex items-center gap-2.5 px-4 py-3 border-b shrink-0">
        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[10px] font-semibold ${style.bg}`}>
          <span className={`w-1.5 h-1.5 rounded-full ${style.dot}`} />
          {chip.short_label}
        </span>
        <div className="flex-1">
          <p className="text-xs text-muted-foreground">{STATUS_LABEL[chip.status] || chip.status}</p>
        </div>
        <button onClick={onClose} className="p-1 rounded hover:bg-accent text-muted-foreground hover:text-foreground">
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto px-4 py-3 space-y-4">
        {/* Status bar */}
        <div className={`rounded-lg p-3 border ${style.bg}`}>
          <div className="flex items-center gap-2">
            <AlertCircle className="w-4 h-4 shrink-0" />
            <div>
              <p className="text-xs font-semibold">{STATUS_LABEL[chip.status]}</p>
              {chip.why_open && <p className="text-[10px] opacity-80 mt-0.5">{chip.why_open}</p>}
              {chip.last_action_date && (
                <p className="text-[10px] opacity-70 mt-0.5 flex items-center gap-1">
                  <Calendar className="w-3 h-3" />
                  Last: {new Date(chip.last_action_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Evidence this visit */}
        {chip.evidence_this_visit?.length > 0 && (
          <div>
            <p className="text-[9px] font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">
              Captured this visit
            </p>
            <div className="space-y-0">
              {chip.evidence_this_visit.map((e, i) => (
                <EvidenceEntry key={i} entry={e} />
              ))}
            </div>
          </div>
        )}

        {/* Actions */}
        {chip.actions_available?.length > 0 && (
          <div className="space-y-2">
            <p className="text-[9px] font-semibold text-muted-foreground uppercase tracking-wider">
              Physician decision required
            </p>
            {chip.evidence_this_visit?.[0]?.snippet && (
              <div className="rounded-lg bg-violet-50 border border-violet-200 p-2.5">
                <p className="text-[9px] text-violet-600 font-semibold mb-1">Patient said:</p>
                <blockquote className="text-xs text-violet-800 italic">
                  "{chip.evidence_this_visit[0].snippet}"
                </blockquote>
              </div>
            )}
            <div className="space-y-1.5">
              {chip.actions_available.map(action => (
                <Button
                  key={action.id}
                  size="sm"
                  variant={action.style === 'default' ? 'default' : action.style === 'outline' ? 'outline' : 'ghost'}
                  className="w-full text-xs justify-start gap-2 h-9"
                  onClick={() => handleAction(action)}
                  disabled={!!executing}
                  data-testid={`chip-action-${action.id}`}>
                  {executing === action.id
                    ? <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                    : <Check className="w-3.5 h-3.5" />}
                  {action.label}
                </Button>
              ))}
            </div>
          </div>
        )}

        {/* No action needed */}
        {chip.status === 'compliant' && (
          <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-3 text-center">
            <Check className="w-5 h-5 text-emerald-500 mx-auto mb-1" />
            <p className="text-xs text-emerald-700 font-medium">Measure satisfied</p>
            <p className="text-[10px] text-emerald-600 mt-0.5">No action needed this period</p>
          </div>
        )}
      </div>
    </div>
  );
}
